
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 15000;
// Voice uploads transit through Django to Azure Blob Storage and can take
// longer than a JSON request on slow mobile networks, so they get their own
// (longer) timeout. Falls back to 60s when not configured.
const VOICE_UPLOAD_TIMEOUT =
  parseInt(process.env.REACT_APP_VOICE_UPLOAD_TIMEOUT, 10) || 60000;
const MAX_RETRIES = 2;

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function withTimeout(promise, ms) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ms);
  return {
    promise: promise(controller.signal).finally(() => clearTimeout(timeout)),
    controller,
  };
}

async function request(path, { retries = MAX_RETRIES } = {}) {
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const { promise } = withTimeout(
        (signal) => fetch(url, { signal, headers: { Accept: 'application/json' } }),
        TIMEOUT
      );
      const response = await promise;

      if (!response.ok) {
        let body = null;
        try { body = await response.json(); } catch { }
        throw new ApiError(
          body?.error || body?.detail || `Erreur serveur (${response.status})`,
          response.status,
          body
        );
      }
      return await response.json();
    } catch (err) {
      lastError = err;
      if (err.name === 'AbortError') {
        lastError = new ApiError('Le serveur ne répond pas (timeout)', 0);
      }
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) throw err;
      if (attempt < retries) await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw lastError;
}

/** Minutes jusqu'à l'heure HH:MM aujourd'hui (approximation). */
function minutesUntilToday(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return 9999;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 9999;
  const now = new Date();
  const target = new Date(now);
  target.setHours(h, m, 0, 0);
  if (target <= now) return 0;
  return Math.round((target - now) / 60000);
}

/**
 * Transforme la réponse GET .../stops/:id/next-passages vers le format attendu par BusSchedule.
 *
 * Format backend :
 *   { stop_name, disruptions,
 *     realtime_source: 'instant_system' | 'gtfs_rt_url',
 *     realtime_available: bool,
 *     // conservé pour compatibilité: miroir de realtime_available
 *     instant_system_available: bool,
 *     lines: [{ id, name, short_name, color, text_color, mode,
 *       directions: [{ display, passages: [{ theoretical_time, realtime_time,
 *         delay_seconds, status, countdown_seconds, is_realtime }] }] }],
 *     gtfs_upcoming: [{ line_name, direction, color, departure_time }] }
 */
export function normalizeNextPassagesPayload(json, stopId) {
  if (json.error === 'no_gtfs_ids') {
    throw new ApiError(
      "Cet arrêt n'a pas d'identifiants GTFS (import ou LineStop manquant).",
      422,
      json
    );
  }

  const fetchedAt = new Date().toISOString();
  let isRealtimeGlobal = false;

  const directions = [];
  const schedulesMap = new Map();

  for (const line of json.lines || []) {
    for (const dir of line.directions || []) {
      const key = `${line.id}__${dir.display}`;

      directions.push({
        key,
        line: line.short_name || line.name,
        direction: dir.display,
        color: line.color || '#607d8b',
        textColor: line.text_color || '#fff',
      });

      const next_buses = [];
      for (const p of dir.passages || []) {
        const displayTime = p.is_realtime && p.realtime_time ? p.realtime_time : p.theoretical_time;
        const minutesAway =
          p.countdown_seconds != null && p.countdown_seconds >= 0
            ? Math.max(0, Math.ceil(p.countdown_seconds / 60))
            : minutesUntilToday(displayTime);

        if (p.is_realtime) isRealtimeGlobal = true;

        next_buses.push({
          scheduled_arrival_time: p.theoretical_time,
          arrival_time: displayTime,
          minutes_away: minutesAway,
          is_realtime: !!p.is_realtime,
          status: p.status,
        });
      }

      schedulesMap.set(key, {
        key,
        line_name: line.short_name || line.name,
        direction: dir.display,
        color: line.color || '#607d8b',
        text_color: line.text_color || '#fff',
        category: line.mode || '',
        next_stops: dir.next_stops || [],
        next_buses,
      });
    }
  }

  // Merge GTFS theoretical departures into the realtime schedules when they
  // describe the same logical (line_name, direction). This collapses what
  // would otherwise be a duplicate "direction tab" whenever the backend
  // returns both a realtime line and a matching theoretical group.
  const findScheduleByLineAndDir = (lineName, direction) => {
    for (const sched of schedulesMap.values()) {
      if (sched.line_name === lineName && sched.direction === direction) {
        return sched;
      }
    }
    return null;
  };

  for (const g of json.gtfs_upcoming || []) {
    let sched = findScheduleByLineAndDir(g.line_name, g.direction);

    if (!sched) {
      const gtfsKey = `gtfs__${g.line_name}__${g.direction}`;
      sched = {
        key: gtfsKey,
        line_name: g.line_name,
        direction: g.direction,
        color: g.color || '#607d8b',
        text_color: '#fff',
        category: '',
        next_stops: g.next_stops || [],
        next_buses: [],
      };
      schedulesMap.set(gtfsKey, sched);
      directions.push({
        key: gtfsKey,
        line: g.line_name,
        direction: g.direction,
        color: g.color || '#607d8b',
        textColor: '#fff',
      });
    }

    if (!sched.next_stops?.length && g.next_stops?.length) {
      sched.next_stops = g.next_stops;
    }

    sched.next_buses.push({
      scheduled_arrival_time: g.departure_time,
      arrival_time: g.departure_time,
      minutes_away: minutesUntilToday(g.departure_time),
      is_realtime: false,
      status: 'theoretical',
    });
  }

  // Sort each schedule's passages chronologically by time-until-arrival so the
  // hero "next passage" card and the countdown stay consistent.
  for (const sched of schedulesMap.values()) {
    sched.next_buses.sort((a, b) => (a.minutes_away ?? 9999) - (b.minutes_away ?? 9999));
  }

  const realtimeAvailable = json.realtime_available ?? json.instant_system_available ?? true;
  const realtimeSource = json.realtime_source || 'instant_system';

  return {
    stop_id: stopId,
    stop_name: json.stop_name || '',
    current_time: fetchedAt,
    is_realtime_global: isRealtimeGlobal,
    realtime_available: realtimeAvailable,
    realtime_source: realtimeSource,
    instant_system_available: realtimeAvailable,
    disruptions: json.disruptions || [],
    directions,
    schedules: Array.from(schedulesMap.values()),
  };
}

/**
 * Soumet un message "Nous Contacter" (réclamation, avis ou demande).
 * @param {{ type: string, name: string, email: string, subject: string, message: string }} payload
 */
export async function submitContact(payload) {
  const url = `${BASE_URL}/contact/`;
  const { promise } = withTimeout(
    (signal) =>
      fetch(url, {
        signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }),
    TIMEOUT
  );
  const response = await promise;
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { }
    throw new ApiError(
      body?.error || body?.detail || `Erreur serveur (${response.status})`,
      response.status,
      body
    );
  }
  return response.status === 204 ? null : await response.json();
}

/**
 * Envoie un message audio (endpoint public voix client).
 * @param {File} audioFile — champ formulaire `audio_file`
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ detail?: string, submission_id: number, status: string }>}
 */
export async function submitVoiceSubmission(
  audioFile,
  stopId,
  options = {}
) {
  const { signal } = options;
  const url = `${BASE_URL}/voice/submissions/`;
  const body = new FormData();
  body.append('audio_file', audioFile);
  if (stopId) {
    body.append('stop_id', stopId);
  }

  const { promise } = withTimeout(
    (sig) =>
      fetch(url, {
        method: 'POST',
        body,
        signal: signal ?? sig,
        headers: { Accept: 'application/json' },
      }),
    VOICE_UPLOAD_TIMEOUT
  );
  const response = await promise;
  if (!response.ok) {
    let resBody = null;
    try {
      resBody = await response.json();
    } catch {
      /* ignore */
    }
    const msg =
      (typeof resBody?.detail === 'string' && resBody.detail) ||
      resBody?.audio_file?.[0] ||
      `Erreur serveur (${response.status})`;
    throw new ApiError(msg, response.status, resBody);
  }
  return response.json();
}

/**
 * @typedef {{ key: string, url?: string }} MobilityButtonConfig
 * @typedef {{ buttons: MobilityButtonConfig[], show_app_stores: boolean, app_store_url: string, google_play_url: string }} MobilityConfig
 */

/**
 * Fetch mobility service buttons configured for this filiale (public API).
 * @returns {Promise<MobilityConfig>}
 */
export async function fetchMobilityServices() {
  return request('/infrastructure/client-front/mobility-services');
}

const api = {
  /**
   * @param {number} stopId
   * @param {string|null} [gtfsStopId] - when provided, restricts results to
   *   that single GTFS stop identifier so the correct platform (side of the
   *   road) is shown to the rider.
   */
  async getSchedule(stopId, gtfsStopId = null) {
    const path = gtfsStopId
      ? `/infrastructure/client-front/stops/${stopId}/${encodeURIComponent(gtfsStopId)}/next-passages`
      : `/infrastructure/client-front/stops/${stopId}/next-passages`;
    const raw = await request(path);
    return normalizeNextPassagesPayload(raw, stopId);
  },
};

export default api;
export { ApiError };
